import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyRevenuePeriods } from '@/service/api/revenue';
import { getRevenueStatusInfo } from '@/utils/revenue';
import { formatDate, formatMoney } from '@/pages/projects/modules/utils';
import DatePicker from '@/components/common/DatePicker';

export default function DailyRevenueTab() {
  const [items, setItems] = useState<Api.Revenue.MyRevenuePeriodResponse[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // 筛选参数
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [projectIdFilter, setProjectIdFilter] = useState<string>('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchMyRevenuePeriods({
        page,
        page_size: pageSize,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        project_id: projectIdFilter || undefined,
      });

      if (res) {
        setItems(res.items || []);
        setTotal(res.total || 0);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载每日收益明细失败');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, startDate, endDate, projectIdFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleResetFilters = () => {
    setStartDate('');
    setEndDate('');
    setProjectIdFilter('');
    setPage(1);
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="space-y-6">
      {/* 筛选条 */}
      <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/70 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-foreground-500 font-medium whitespace-nowrap shrink-0">收益日期:</span>
            <div className="w-36 shrink-0">
              <DatePicker
                value={startDate}
                maxDate={endDate || undefined}
                onChange={(val) => {
                  setStartDate(val);
                  if (endDate && val && val > endDate) {
                    setEndDate('');
                  }
                  setPage(1);
                }}
                placeholder="开始日期"
                size="sm"
              />
            </div>
            <span className="text-foreground-400 shrink-0">至</span>
            <div className="w-36 shrink-0">
              <DatePicker
                value={endDate}
                minDate={startDate || undefined}
                onChange={(val) => {
                  if (startDate && val && val < startDate) {
                    alert('结束日期不能小于开始日期');
                    return;
                  }
                  setEndDate(val);
                  setPage(1);
                }}
                placeholder="结束日期"
                size="sm"
              />
            </div>
          </div>

          {(startDate || endDate || projectIdFilter) && (
            <button
              onClick={handleResetFilters}
              className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
            >
              重置筛选
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-foreground-700 font-medium flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
            刷新数据
          </button>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-600 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={loadData} className="underline hover:text-rose-700 cursor-pointer">重试</button>
        </div>
      )}

      {/* 数据列表 / 表格 */}
      {loading ? (
        <div className="py-20 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-loader-4-line text-3xl block mb-2 animate-spin text-primary-500"></i>
          正在加载每日收益明细...
        </div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-foreground-400 text-sm glass-card rounded-xl">
          <i className="ri-bar-chart-2-line text-3xl block mb-2 text-foreground-300"></i>
          暂无符合条件的每日收益明细记录
        </div>
      ) : (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-gray-200/70 bg-white shadow-sm">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50/80 border-b border-gray-200/70 text-foreground-500 font-medium whitespace-nowrap">
                <tr>
                  <th className="py-3 px-4">收益日期</th>
                  <th className="py-3 px-4">发行项目</th>
                  <th className="py-3 px-4">渠道</th>
                  <th className="py-3 px-4 text-right">总收入</th>
                  <th className="py-3 px-4 text-right">渠道扣除</th>
                  <th className="py-3 px-4 text-right">预估净收益</th>
                  <th className="py-3 px-4 text-center">状态</th>
                  <th className="py-3 px-4 text-center">来源文件</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => {
                  const statusInfo = getRevenueStatusInfo(item.status);
                  const grossVal = item.gross_income_settlement ?? item.gross_income_original ?? 0;
                  const deductionVal = item.channel_deduction_settlement ?? item.channel_deduction_original ?? 0;
                  const netVal = Math.max(0, grossVal - deductionVal);
                  const currency = item.settlement_currency || item.original_currency || 'USD';

                  return (
                    <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 px-4 font-mono font-medium text-foreground-900 whitespace-nowrap">
                        {formatDate(item.revenue_date)}
                      </td>
                      <td className="py-3 px-4 font-medium text-foreground-900">
                        {item.project_id ? (
                          <Link
                            to={`/projects/${item.project_id}`}
                            className="text-primary-600 hover:text-primary-700 hover:underline font-semibold block"
                            title="点击查看项目详情"
                          >
                            {item.project_title || item.project_no || '查看项目详情'}
                          </Link>
                        ) : (
                          <div>{item.project_title || item.project_no || '-'}</div>
                        )}
                        {item.project_no && (
                          <span className="text-[10px] text-foreground-400 font-mono block mt-0.5">
                            {item.project_no}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {item.channel_name ? (
                          <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                            {item.channel_name}
                          </span>
                        ) : (
                          <span className="text-foreground-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-foreground-900 whitespace-nowrap">
                        {formatMoney(grossVal, currency)}
                      </td>
                      <td className="py-3 px-4 text-right font-medium text-rose-600 whitespace-nowrap">
                        {deductionVal > 0 ? `-${formatMoney(deductionVal, currency)}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-emerald-600 whitespace-nowrap">
                        {formatMoney(netVal, currency)}
                      </td>
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center whitespace-nowrap">
                        {item.source_asset?.access_url ? (
                          <a
                            href={item.source_asset.access_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium underline"
                          >
                            <i className="ri-attachment-line"></i>
                            {item.source_asset.file_name || '查看数据源'}
                          </a>
                        ) : (
                          <span className="text-foreground-400 text-[11px]">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 分页控制 */}
          <div className="flex items-center justify-between pt-2 px-1 text-xs text-foreground-500">
            <div>
              共 <strong className="text-foreground-800">{total}</strong> 条每日收益明细
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                上一页
              </button>
              <span className="font-medium text-foreground-700">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
